import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useDragControls, useReducedMotion } from "framer-motion";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import { useNavigate } from "react-router-dom";
import api from "../../api/apiHelper";
import { readApiCache, writeApiCache } from "../../utils/apiCache";
import Page from "../molecules/Page";
import { RenderMarkdownWithLatex, truncateExample } from "../../utils/utils";
import SurefirePaywallPopup from "../molecules/SurefirePaywallPopup";
import SessionSetupPopup from "../molecules/SessionSetupPopup";
import { isNoAccessError } from "../../utils/access";
import { FaGraduationCap, FaLock } from "react-icons/fa";
import {
  FiCheckCircle, FiClock, FiAlertTriangle, FiZap, FiInfo, FiChevronRight,
  FiChevronDown, FiPlay, FiActivity, FiLayers, FiFolder, FiCpu, FiTarget, FiX, FiPackage,
} from "react-icons/fi";

// ==============================================================================
// 1. KONFIGURACJA
// ==============================================================================
// Backend zwraca LEARNING_IN_PROGRESS / CONSOLIDATION_READY — mapujemy na wspólne
// nazwy faz (identyczne jak w Pewniakach), żeby reużyć ten sam język wizualny.
const normStatus = (s) =>
  s === "LEARNING_IN_PROGRESS" ? "PHASE1_IN_PROGRESS"
  : s === "CONSOLIDATION_READY" ? "PHASE2_READY"
  : s;

const STATUS_HINTS = {
  MASTERED: "Ten typ siedzi już w pamięci długotrwałej. Możesz go powtórzyć w dowolnym momencie.",
  DECAYING: "Dawno nie ćwiczono — wiedza zaczyna zanikać. Rozwiąż, żeby ją odświeżyć!",
  WAITING: "Twój mózg konsoliduje wiedzę. Przerwa między fazami jest kluczowa.",
  PHASE1_IN_PROGRESS: "Faza 1: uczysz się rozwiązywać ten typ. Zbierz wymagane poprawne odpowiedzi.",
  PHASE2_READY: "Faza 1 zaliczona! Utrwal typ, żeby trafił do pamięci długotrwałej.",
  NOT_STARTED: "Jeszcze nieruszane. Kliknij Start, żeby zacząć od pierwszego zadania.",
};

const SECTION_CONFIG = {
  decaying:      { label: "Do powtórki",           description: "Wymagają powtórki",        icon: <FiAlertTriangle />, colorClass: "text-[#8A0000]", dotClass: "bg-[#8A0000]" },
  consolidation: { label: "Do utrwalenia",          description: "W trakcie utrwalania",     icon: <FiZap />,           colorClass: "text-[#E09F2E]", dotClass: "bg-[#E09F2E]" },
  learning:      { label: "W trakcie nauki",        description: "Aktywnie ćwiczone",        icon: <FiActivity />,      colorClass: "text-[#233855]", dotClass: "bg-[#233855]" },
  waiting:       { label: "Odpoczywają (Pauza)",    description: "Czekają na kolejną sesję", icon: <FiClock />,         colorClass: "text-gray-400",  dotClass: "bg-gray-300"  },
  mastered:      { label: "W pamięci długotrwałej", description: "Opanowane",                icon: <FiCheckCircle />,   colorClass: "text-[#007D00]", dotClass: "bg-[#007D00]" },
  not_started:   { label: "Do zrobienia",           description: "Jeszcze nierozpoczęte",    icon: <FiLayers />,        colorClass: "text-gray-400",  dotClass: "bg-gray-200"  },
};

const LOADING_STEPS = ["dobieramy zadania rozszerzenia", "przeszukujemy kartę wzorów CKE", "przygotowujemy Orła AI"];

const pluralNiepewniak = (n) => {
  if (n === 1) return "Niepewniak";
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return "Niepewniaki";
  return "Niepewniaków";
};

// ==============================================================================
// 2. KOMPONENTY UI
// ==============================================================================
const NiepewniakiBenefits = () => (
  <div className="flex flex-col gap-2.5 mb-5">
    <div className="flex items-center gap-2.5">
      <FiCpu className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Rozszerzenie:</strong> Typy zadań warte 40–60% punktów arkusza PR.</p>
    </div>
    <div className="flex items-center gap-2.5">
      <FiTarget className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Pamięć długotrwała:</strong> Utrwalasz metodę, nie zapomnisz na maturze.</p>
    </div>
  </div>
);

const PhaseLabel = ({ status, progress, learningTarget }) => {
  const config = {
    PHASE1_IN_PROGRESS: { text: `Faza 1 · ${progress}/${learningTarget}`, color: "text-[#233855] bg-[#233855]/8 border-[#233855]/15" },
    PHASE2_READY:       { text: "Faza 2 — do utrwalenia",                 color: "text-[#E09F2E] bg-[#E09F2E]/10 border-[#E09F2E]/20" },
    WAITING:            { text: "Mózg konsoliduje",                        color: "text-gray-400 bg-gray-100 border-gray-200" },
    MASTERED:           { text: "Opanowane",                               color: "text-[#007D00] bg-[#007D00]/8 border-[#007D00]/20" },
    DECAYING:           { text: "Wiedza zanika!",                          color: "text-[#8A0000] bg-[#8A0000]/8 border-[#8A0000]/15" },
    NOT_STARTED:        { text: "Nierozpoczęte",                           color: "text-gray-400 bg-gray-100 border-gray-200" },
  };
  const c = config[status] || config.NOT_STARTED;
  return (
    <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border mt-2 ${c.color}`}>
      {c.text}
    </span>
  );
};

// Dynamiczny tor postępu: learningTarget kółek + przerwa + consolidationTarget kółek.
const ProgressTrack = ({ progress, status, learningTarget, consolidationTarget }) => {
  const isDecaying = status === "DECAYING";
  const isMastered = status === "MASTERED";
  const getStepStyle = (index) => {
    const isDone = progress > index;
    const isCurrent = progress === index;
    if (isDone) {
      if (isDecaying) return "bg-[#8A0000] border-[#8A0000]";
      if (isMastered) return "bg-[#007D00] border-[#007D00]";
      return "bg-[#E09F2E] border-[#E09F2E]";
    }
    if (isCurrent) return "bg-[#E09F2E]/20 border-[#E09F2E] border-2";
    return "bg-white border-gray-200 border";
  };
  const phase1 = Array.from({ length: learningTarget }, (_, i) => i);
  const phase2 = Array.from({ length: consolidationTarget }, (_, i) => learningTarget + i);
  const Circle = (i) => (
    <div key={i} className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200 ${getStepStyle(i)}`}>
      {progress > i && <FiCheckCircle className="text-white" size={9} strokeWidth={3} />}
    </div>
  );
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <div className="flex items-center gap-1">{phase1.map(Circle)}</div>
      <div className="w-1 h-1 rounded-full bg-gray-200 mx-0.5" />
      <div className="flex items-center gap-1">{phase2.map(Circle)}</div>
    </div>
  );
};

const PhaseIndicator = ({ item }) => {
  const { status, progress, learning_target, consolidation_target } = item;
  if (status === "PHASE1_IN_PROGRESS" || status === "PHASE2_READY") {
    return <ProgressTrack progress={progress} status={status} learningTarget={learning_target} consolidationTarget={consolidation_target} />;
  }
  if (status === "DECAYING" || status === "MASTERED") return null;
  return <PhaseLabel status={status} progress={progress} learningTarget={learning_target} />;
};

const DOT_STATUS_COLOR = {
  MASTERED: "bg-[#007D00] border-[#007D00]",
  DECAYING: "bg-[#8A0000] border-[#8A0000]",
  PHASE2_READY: "bg-[#E09F2E] border-[#E09F2E]",
  PHASE1_IN_PROGRESS: "bg-[#233855]/60 border-[#233855]/60",
  WAITING: "bg-gray-300 border-gray-300",
  NOT_STARTED: "bg-gray-200 border-gray-200",
};

const ROADMAP_SECTIONS = [
  { type: "mastered",      statusFilter: ["MASTERED"] },
  { type: "decaying",      statusFilter: ["DECAYING"] },
  { type: "consolidation", statusFilter: ["PHASE2_READY"] },
  { type: "learning",      statusFilter: ["PHASE1_IN_PROGRESS"] },
  { type: "waiting",       statusFilter: ["WAITING"] },
  { type: "not_started",   statusFilter: ["NOT_STARTED"] },
];

const NiepewniakiRoadmapModal = ({ isOpen, onClose, items, onStart }) => {
  const [expandedId, setExpandedId] = useState(null);
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent("bottomsheet:open"));
      setExpandedId(null);
      return () => window.dispatchEvent(new CustomEvent("bottomsheet:close"));
    }
  }, [isOpen]);
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(isOpen);
  const masteredCount = items.filter((s) => s.status === "MASTERED").length;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div key="niep-backdrop" {...getSheetBackdropMotion()} className="fixed inset-0 bg-black/50 z-[9990]" onClick={onClose} />
          <motion.div
            key="niep-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y" dragControls={dragControls} dragListener={false}
            dragConstraints={{ top: 0 }} dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none" onPointerDown={(e) => dragControls.start(e)}>
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 pt-2 pb-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="font-apotek text-lg text-[#233855] tracking-wide uppercase leading-none">Kolekcja Niepewniaków</h2>
                <p className="text-xs text-gray-400 mt-0.5">{masteredCount} z {items.length} opanowanych</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <FiX size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-6">
              {ROADMAP_SECTIONS.map(({ type, statusFilter }) => {
                const list = items.filter((s) => statusFilter.includes(s.status));
                if (list.length === 0) return null;
                const cfg = SECTION_CONFIG[type];
                const isDim = type === "not_started";
                const isMastered = type === "mastered";
                const actionBtn = (() => {
                  if (type === "waiting") return null;
                  if (type === "decaying") return { label: "Napraw teraz", cls: "bg-[#8A0000] text-white hover:bg-[#8A0000]/90" };
                  if (type === "consolidation") return { label: "Utrwal", cls: "bg-[#E09F2E] text-white hover:bg-[#E09F2E]/90" };
                  if (type === "mastered") return { label: "Powtórz", cls: "border border-[#007D00]/30 text-[#007D00] bg-transparent hover:bg-[#007D00]/5" };
                  return { label: "Start", cls: "bg-[#233855] text-white hover:bg-[#233855]/90" };
                })();
                return (
                  <div key={type} className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isMastered ? "bg-[#007D00]" : cfg.dotClass}`} />
                      <span className={`text-xs font-bold uppercase tracking-widest ${cfg.colorClass}`}>{cfg.label}</span>
                      <span className="text-[11px] text-gray-300 font-medium">{list.length}</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {list.map((item) => {
                        const isExpanded = expandedId === item.id;
                        return (
                          <motion.div key={item.id} layout className={isExpanded ? "col-span-2" : ""} transition={{ type: "spring", bounce: 0, duration: 0.35 }}>
                            <motion.button
                              onClick={() => setExpandedId(isExpanded ? null : item.id)}
                              whileTap={{ scale: 0.94 }}
                              className={`w-full relative overflow-hidden text-left rounded-xl border transition-colors ${
                                isExpanded ? "p-4 bg-white border-gray-200"
                                : isDim ? "p-3 bg-gray-50 border-gray-100 opacity-50"
                                : "p-3 bg-white border-gray-200 hover:border-[#233855]/20 hover:shadow-sm"
                              }`}
                              style={isMastered ? { borderColor: "#007D00" } : undefined}
                            >
                              {isExpanded ? (
                                <div>
                                  <p className="text-sm font-semibold text-[#233855] leading-snug mb-1">{item.title}</p>
                                  {item.dzial && (
                                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-2.5">
                                      <FiFolder size={9} /><span>{item.dzial}</span>
                                    </div>
                                  )}
                                  {(item.points || (item.sources && item.sources.length > 0)) && (
                                    <div className="flex flex-wrap items-center gap-1 mb-2.5">
                                      {item.points && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold">
                                          {String(item.points).replace(" pkt", "")} pkt
                                        </span>
                                      )}
                                      {item.sources && item.sources.map((src, idx) => (
                                        <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500">
                                          <FaGraduationCap className="text-[#233855]" size={7} />
                                          {src.replace("Zadanie", "Zad.")}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {item.exampleProblem && (
                                    <div className="mb-3">
                                      <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1 flex items-center gap-1">
                                        <FaGraduationCap size={8} /> Przykładowe zadanie
                                      </p>
                                      <div className="text-[12px] text-gray-600 rounded-lg p-2.5 border bg-white border-gray-100">
                                        <RenderMarkdownWithLatex text={truncateExample(item.exampleProblem)} />
                                      </div>
                                    </div>
                                  )}
                                  {actionBtn && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); onClose(); onStart(item); }}
                                      className={`w-full py-1.5 rounded-lg text-xs font-bold font-apotek tracking-wider uppercase active:scale-[0.98] transition-all ${actionBtn.cls}`}
                                    >
                                      {actionBtn.label}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center text-center">
                                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 text-[17px] ${isMastered ? "bg-[#233855] text-[#E09F2E]" : isDim ? "bg-gray-100/70 text-gray-300" : `bg-gray-50 ${cfg.colorClass}`}`}>
                                    {isMastered ? <FiCheckCircle size={18} strokeWidth={2.5} /> : cfg.icon}
                                  </div>
                                  <p className={`text-[13px] font-medium leading-tight line-clamp-2 ${isDim ? "text-gray-400" : "text-[#233855]"}`}>{item.title}</p>
                                </div>
                              )}
                            </motion.button>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

const NiepewniakiWidget = ({ stats, items, onOpenRoadmap }) => {
  const pct = stats.total > 0 ? Math.round((stats.mastered / stats.total) * 100) : 0;
  const isCompleted = stats.mastered === stats.total && stats.total > 0;
  return (
    <div
      onClick={onOpenRoadmap}
      className={`group relative w-full rounded-xl shadow-[0_12px_38px_-6px_rgba(35,56,85,0.30)] cursor-pointer transform transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_22px_48px_-8px_rgba(35,56,85,0.36)] p-[1px] bg-gradient-to-br ${isCompleted ? "from-[#E09F2E] via-[#F8AB3B] to-[#E09F2E]" : "from-[#E09F2E]/50 via-[#233855]/50 to-[#E09F2E]/50"}`}
    >
      <div className="relative w-full bg-white rounded-[calc(0.75rem-1px)] overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl shadow-md text-white transition-colors duration-300 ${isCompleted ? "bg-[#E09F2E]" : "bg-[#233855]"}`}>
                <FiPackage className="text-xl text-[#F8AB3B]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-0.5">Kolekcja Niepewniaków</p>
                <h3 className="text-xl font-bold font-apotek tracking-wide text-[#233855] leading-none">{stats.mastered} / {stats.total}</h3>
              </div>
            </div>
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
              <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
            </div>
          </div>
          <div className="w-full h-3.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
            <div className="h-full rounded-full bg-gradient-to-r from-[#FFD700] to-[#F8AB3B] shadow-[0_0_10px_rgba(248,171,59,0.3)] relative overflow-hidden transition-all duration-1000 ease-out" style={{ width: `${pct}%` }}>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/40 to-transparent opacity-50" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-3">
            {items.map((s) => (
              <div key={s.id} className={`w-3 h-3 rounded-full border ${DOT_STATUS_COLOR[s.status] || DOT_STATUS_COLOR.NOT_STARTED}`} />
            ))}
          </div>
        </div>
        <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-[0.03] ${isCompleted ? "bg-[#E09F2E]" : "bg-[#F8AB3B]"}`} />
      </div>
    </div>
  );
};

const NiepewniakRow = ({ item, onStart }) => {
  const isWaiting = item.status === "WAITING";
  const isDecaying = item.status === "DECAYING";
  const isPhase2Ready = item.status === "PHASE2_READY";
  const isMastered = item.status === "MASTERED";
  const isLocked = item.is_locked;

  const getButtonConfig = () => {
    if (isLocked)      return { label: "Wymagane Premium", disabled: false, className: "bg-gray-100 text-gray-500 border border-gray-200 hover:bg-[#E09F2E]/10 hover:border-[#E09F2E]/30 hover:text-[#E09F2E]" };
    if (isWaiting)     return { label: `Wróć za ${item.wait_time}`, disabled: true, className: "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed" };
    if (isMastered)    return { label: "Powtórz", disabled: false, className: "bg-white text-[#007D00] border border-[#007D00]/30 hover:bg-[#007D00]/5" };
    if (isDecaying)    return { label: "Napraw teraz", disabled: false, className: "bg-[#8A0000] text-white hover:bg-[#8A0000]/90" };
    if (isPhase2Ready) return { label: "Utrwal", disabled: false, className: "bg-[#E09F2E] text-white hover:bg-[#E09F2E]/90" };
    return { label: "Start", disabled: false, className: "bg-[#233855] text-white hover:bg-[#233855]/90" };
  };
  const btnCfg = getButtonConfig();

  return (
    <div className="border-b border-gray-100 last:border-0 py-4">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-[#233855] leading-snug">{item.title}</p>
            {isLocked && <FaLock className="text-[#E09F2E] text-xs flex-shrink-0" />}
          </div>
          <div className="mt-1"><PhaseIndicator item={item} /></div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
        <span className="flex items-center gap-1"><FiFolder size={10} />{item.dzial}</span>
        <span className="flex items-center gap-1"><FiLayers size={10} />{item.templates_count} typy zadań</span>
      </div>

      {(item.points || (item.sources && item.sources.length > 0)) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {item.points && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold whitespace-nowrap">
              {String(item.points).replace(" pkt", "")} pkt
            </div>
          )}
          {item.sources && item.sources.map((src, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500 whitespace-nowrap">
              <FaGraduationCap className="text-[#233855]" size={8} />
              {src.replace("Zadanie", "Zad.")}
            </span>
          ))}
        </div>
      )}

      {item.exampleProblem && (
        <div className="mb-3 relative">
          {isLocked && (
            <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
              <FaLock className="text-gray-300 text-2xl" />
            </div>
          )}
          <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1">Przykładowe zadanie</p>
          <div className={`p-2.5 bg-white rounded-lg text-sm text-gray-600 border border-gray-100 ${isLocked ? "blur-[0.5px]" : ""}`}>
            <RenderMarkdownWithLatex text={truncateExample(item.exampleProblem)} />
          </div>
        </div>
      )}

      {isWaiting && (
        <div className="flex items-center gap-2 bg-[#233855]/5 rounded-lg px-3 py-2 mb-3 border border-[#233855]/10">
          <FiClock className="text-[#233855] stroke-[2.5] shrink-0" size={14} />
          <p className="text-[11px] text-[#233855] font-medium">Wróć za <span className="font-bold">{item.wait_time}</span></p>
        </div>
      )}

      <button
        onClick={() => { if (!btnCfg.disabled) onStart(item); }}
        disabled={btnCfg.disabled}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold font-apotek tracking-wider uppercase transition-all active:scale-[0.98] ${btnCfg.className}`}
      >
        {isLocked ? <FaLock size={11} /> : (!btnCfg.disabled ? <FiPlay className="stroke-[2.5]" size={13} /> : <FiClock className="stroke-[2.5]" size={13} />)}
        {btnCfg.label}
      </button>

      <div className="flex items-start gap-1.5 mt-2">
        <FiInfo className="text-gray-300 stroke-[2] shrink-0 mt-[1px]" size={11} />
        <p className="text-xs text-gray-400 leading-relaxed">{STATUS_HINTS[item.status]}</p>
      </div>
    </div>
  );
};

const NiepewniakSheet = ({ stage, items, onClose, onStart }) => {
  const stageRef = useRef(stage);
  const itemsRef = useRef(items);
  useEffect(() => {
    if (stage) {
      window.dispatchEvent(new CustomEvent("bottomsheet:open"));
      return () => window.dispatchEvent(new CustomEvent("bottomsheet:close"));
    }
  }, [stage]);
  if (stage) { stageRef.current = stage; itemsRef.current = items; }
  const currentStage = stageRef.current;
  const currentItems = itemsRef.current || [];
  const cfg = currentStage ? SECTION_CONFIG[currentStage] : null;
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(!!(stage && cfg));

  return ReactDOM.createPortal(
    <AnimatePresence>
      {stage && cfg && (
        <>
          <motion.div key="nsf-backdrop" {...getSheetBackdropMotion()} className="fixed inset-0 bg-black/50 z-[9990]" onClick={onClose} />
          <motion.div
            key="nsf-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y" dragControls={dragControls} dragListener={false}
            dragConstraints={{ top: 0 }} dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none" onPointerDown={(e) => dragControls.start(e)}>
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center gap-3 px-5 pt-2 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex-1">
                <h2 className={`font-apotek tracking-[0.05em] text-[16px] uppercase leading-none ${cfg.colorClass}`}>{cfg.label}</h2>
                <p className="text-xs text-gray-400 mt-1">{currentItems.length} {pluralNiepewniak(currentItems.length)}</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <FiX size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-6 space-y-3">
              {currentItems.map((item) => <NiepewniakRow key={item.id} item={item} onStart={onStart} />)}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

const STAGE_BORDER_COLOR = {
  decaying: "border-[#8A0000]/30",
  consolidation: "border-[#E09F2E]/40",
  learning: "border-[#233855]/25",
  waiting: "border-gray-200",
  mastered: "border-[#007D00]/30",
  not_started: "border-gray-200",
};

const NiepewniakStageCard = ({ type, items, onOpen }) => {
  if (items.length === 0) return null;
  const cfg = SECTION_CONFIG[type];
  return (
    <button
      type="button"
      onClick={() => onOpen(type)}
      className={`w-full text-left p-3.5 mb-2.5 bg-white border ${STAGE_BORDER_COLOR[type]} rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.97] active:shadow-sm transition-all duration-150 group flex items-center gap-3`}
    >
      <div className="flex-shrink-0 p-2 rounded-xl shadow-md bg-[#233855]">
        {React.cloneElement(cfg.icon, { size: 15, className: "text-[#E09F2E]" })}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-[#233855] leading-snug truncate">{cfg.label}</span>
          {type === "decaying" && (
            <span className="relative flex-shrink-0 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8A0000] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8A0000]" />
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400">{items.length} {pluralNiepewniak(items.length)} · {cfg.description.toLowerCase()}</p>
      </div>
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
      </div>
    </button>
  );
};

// ==============================================================================
// 3. EKRAN
// ==============================================================================
const NiepewniakiScreen = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(() => readApiCache("niepewniaki", Infinity) || []);
  const [loading, setLoading] = useState(!readApiCache("niepewniaki", Infinity));
  const fetchGuard = useRef(false);

  const [selectedStage, setSelectedStage] = useState(null);
  const [isRoadmapOpen, setIsRoadmapOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(() => {
    const saved = localStorage.getItem("niepewniaki_benefits_open");
    return saved !== null ? saved === "true" : true;
  });

  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSessionDataReady, setIsSessionDataReady] = useState(false);
  const [sessionMessages, setSessionMessages] = useState(null);
  const sessionDataRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("niepewniaki_benefits_open", String(isBenefitsOpen));
  }, [isBenefitsOpen]);

  useEffect(() => {
    if (fetchGuard.current) return;
    fetchGuard.current = true;
    const hasFresh = !!readApiCache("niepewniaki");
    if (hasFresh) { setLoading(false); return; }
    const hasAny = !!readApiCache("niepewniaki", Infinity);
    api.get("niepewniaki/", hasAny ? { skipLoading: true } : {})
      .then((res) => {
        const norm = (res.data.items || []).map((it) => ({ ...it, status: normStatus(it.status) }));
        setItems(norm);
        writeApiCache("niepewniaki", norm);
      })
      .catch((err) => console.error("Błąd pobierania niepewniaków:", err))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => ({
    total: items.length,
    mastered: items.filter((s) => s.status === "MASTERED").length,
  }), [items]);

  const groupedItems = useMemo(() => ({
    decaying: items.filter((s) => s.status === "DECAYING"),
    consolidation: items.filter((s) => s.status === "PHASE2_READY"),
    learning: items.filter((s) => s.status === "PHASE1_IN_PROGRESS"),
    waiting: items.filter((s) => s.status === "WAITING"),
    mastered: items.filter((s) => s.status === "MASTERED"),
    not_started: items.filter((s) => s.status === "NOT_STARTED"),
  }), [items]);

  const handleStart = (item) => {
    if (item.is_locked) { setIsPaywallOpen(true); return; }
    setSessionMessages([`Ładujemy Niepewniaka: ${item.title}`, ...LOADING_STEPS]);
    setIsSessionLoading(true);
    setIsSessionDataReady(false);
    sessionDataRef.current = null;
    api.post(`niepewniaki/${item.id}/start/`, {}, { skipLoading: true })
      .then((res) => {
        const { final_problems, user_elo_dict, niepewniak_info } = res.data;
        if (!final_problems || final_problems.length === 0) {
          setIsSessionLoading(false);
          alert("Brak zadań dla tego niepewniaka.");
          return;
        }
        sessionDataRef.current = { final_problems, user_elo_dict, niepewniak_info };
        setIsSessionDataReady(true);
      })
      .catch((err) => {
        console.error("Nie udało się rozpocząć niepewniaka:", err);
        setIsSessionLoading(false);
        // Serwer bramkuje niepewniaki dostępem do rozszerzenia (402) — pokaż paywall,
        // nie ciszę, gdy lista przyszła z nieaktualnym is_locked.
        if (isNoAccessError(err)) setIsPaywallOpen(true);
      });
  };

  const handleSessionComplete = () => {
    const data = sessionDataRef.current;
    sessionDataRef.current = null;
    if (data && data.final_problems) {
      // Ten sam kontrakt co pewniaki — ekran rozwiazywania stoi na useProblemSolver.
      localStorage.removeItem("problemsSession");
      localStorage.setItem("problemsData", JSON.stringify(data.final_problems));
      localStorage.setItem("userEloDict", JSON.stringify(data.user_elo_dict || {}));
      localStorage.setItem("currentNiepewniak", JSON.stringify(data.niepewniak_info));
      navigate("/solve-niepewniak");
    } else {
      setIsSessionLoading(false);
      setIsSessionDataReady(false);
    }
  };

  const handleSubscribe = () => { setIsPaywallOpen(false); navigate("/upgrade"); };

  const stageOrder = ["mastered", "decaying", "consolidation", "learning", "waiting", "not_started"];

  return (
    <Page showNav={true}>
      <div className="bg-white lg:bg-transparent w-full">
        <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
          <div className="w-full lg:px-10 py-4 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200 flex flex-col gap-5">
            <div className="text-center">
              <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">Niepewniaki Rozszerzenia</h1>
            </div>
            <div>
              <button type="button" onClick={() => setIsBenefitsOpen((v) => !v)} className="flex items-center justify-between w-full mb-2 group">
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">Dlaczego to działa?</span>
                <FiChevronDown className={`text-gray-300 group-hover:text-gray-500 transition-all duration-200 ${isBenefitsOpen ? "rotate-180" : ""}`} size={14} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isBenefitsOpen ? "max-h-52 opacity-100" : "max-h-0 opacity-0"}`}>
                <NiepewniakiBenefits />
              </div>
            </div>

            <NiepewniakiWidget stats={stats} items={items} onOpenRoadmap={() => setIsRoadmapOpen(true)} />

            {items.length === 0 && !loading ? (
              <div className="text-center py-10"><p className="text-gray-400">Brak niepewniaków w bazie.</p></div>
            ) : (
              <div>
                {stageOrder.map((type) => (
                  <NiepewniakStageCard key={type} type={type} items={groupedItems[type]} onOpen={setSelectedStage} />
                ))}
              </div>
            )}

            <NiepewniakSheet
              stage={selectedStage}
              items={selectedStage ? groupedItems[selectedStage] : []}
              onClose={() => setSelectedStage(null)}
              onStart={(item) => { setSelectedStage(null); handleStart(item); }}
            />
          </div>
          <div className="h-[13vh] flex-shrink-0" />
        </div>
      </div>

      <NiepewniakiRoadmapModal
        isOpen={isRoadmapOpen}
        onClose={() => setIsRoadmapOpen(false)}
        items={items}
        onStart={(item) => { setIsRoadmapOpen(false); handleStart(item); }}
      />
      <SurefirePaywallPopup variant="niepewniak" isOpen={isPaywallOpen} onClose={() => setIsPaywallOpen(false)} onSubscribe={handleSubscribe} />
      <SessionSetupPopup isOpen={isSessionLoading} isDataReady={isSessionDataReady} onComplete={handleSessionComplete} messages={sessionMessages} />
    </Page>
  );
};

export default NiepewniakiScreen;
